import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { isAppReady, ORGANISATIONS, getAll, TENANTS } from '../../store/reducers';
import { auth } from '../../store/auth/auth.selectors';
import { filter, withLatestFrom, map } from 'rxjs/operators';
import {
  ADMIN_ROLE,
  GLOBAL_ROLES,
  ORGANISATION_ADMIN_ROLE,
  TENANT_ADMINISTRATOR,
} from '@careapp-group/core';
import { OrganisationDto, TenantDto } from '@careapp-group/ng-sdk';

/**
 * User must have ORG_ADMIN role of more than 1 organisation,
 * or be a global admin
 */
@Injectable()
export class GroupAdminGuard  {
  private appReady: Observable<boolean>;
  private authenticated: Observable<boolean>;
  private globalRoles: Observable<string[]>;
  private organisations: Observable<OrganisationDto[]>;
  private tenants: Observable<TenantDto[]>;
  constructor(private store: Store<any>) {
    this.appReady = this.store.select(isAppReady);
    this.globalRoles = this.store.select(auth.getRoles);
    this.authenticated = this.store.select(auth.getLoggedIn);
    this.organisations = this.store.select(getAll(ORGANISATIONS));
    this.tenants = this.store.select(getAll(TENANTS));
  }

  public canActivate(): Observable<boolean> {
    return this.appReady.pipe(
      filter((ready) => ready),
      withLatestFrom(this.authenticated, this.globalRoles, this.organisations, this.tenants),
      map(([ready, loggedIn, globalRoles, organisations, tenants]) => {
        if (!loggedIn) {
          return false;
        }

        if (globalRoles && globalRoles.some((r) => GLOBAL_ROLES.includes(r))) {
          return true;
        }

        // allow if they are a tenant admin
        if (tenants && tenants.some((t) => t.permission?.role === TENANT_ADMINISTRATOR)) {
          return true;
        }

        // Count how many organisations they are an admin of
        const adminCount = organisations.reduce((count: number, org: OrganisationDto) => {
          if (org.roles && org.roles.includes(ORGANISATION_ADMIN_ROLE)) {
            return ++count;
          }
          return count;
        }, 0);
        // must be an admin of multiple
        return adminCount > 1;
      })
    );
  }
}
